import { useCallback, useEffect, useRef, useState } from 'react';

import { t } from '../constants';
import { deleteTag, fetchTag, removeTagFromType, renameTag } from '../services/TagsService';

const messagesFrom = (error, fallback) => {
  const messages = error?.response?.data?.errors;

  return Array.isArray(messages) && messages.length > 0 ? messages : [fallback];
};

// IP-15330: everything the tag Brushaway does to one tag.
//
// The drawer reads its own detail rather than reusing the grid row: the usage
// breakdown has to be re-read after a detach, and a deep link (/settings/tags/:id/edit)
// can name a tag that is not on whichever page the grid loaded.
export default function useTagDetail({ tagId, onMutated, onClose }) {
  const [tag, setTag] = useState(null);
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);

  const apply = useCallback((payload) => {
    setTag(payload);
    setName(payload.name);
  }, []);

  // Opening tag A then tag B before A's response lands can resolve out of order,
  // leaving B's id in state next to A's name — and a rename would then apply A's
  // name to B. Only the newest request may write.
  const requestId = useRef(0);

  const load = useCallback(async () => {
    const thisRequest = requestId.current + 1;
    requestId.current = thisRequest;

    setLoading(true);
    setErrors([]);

    try {
      const payload = await fetchTag(tagId);

      if (requestId.current !== thisRequest) return;
      apply(payload);
    } catch (error) {
      if (requestId.current !== thisRequest) return;
      setErrors(messagesFrom(error, t('drawer.errors.load')));
    } finally {
      if (requestId.current === thisRequest) setLoading(false);
    }
  }, [tagId, apply]);

  useEffect(() => {
    if (!tagId) {
      requestId.current += 1;
      setTag(null);
      setName('');
      setErrors([]);
      return;
    }

    // eslint-disable-next-line no-void
    void load();
  }, [tagId, load]);

  const rename = useCallback(async () => {
    setSaving(true);
    setErrors([]);

    try {
      apply(await renameTag(tagId, name.trim()));
      onMutated();
      onClose();
    } catch (error) {
      setErrors(messagesFrom(error, t('drawer.errors.rename')));
    } finally {
      setSaving(false);
    }
  }, [tagId, name, apply, onMutated, onClose]);

  // Stays open afterwards: the point of detaching one type is to look at what is
  // left, so the refreshed breakdown is the result the user came for.
  const detach = useCallback(
    async (taggableType) => {
      setSaving(true);
      setErrors([]);

      try {
        apply(await removeTagFromType(tagId, taggableType));
        onMutated();
      } catch (error) {
        setErrors(messagesFrom(error, t('drawer.errors.detach')));
      } finally {
        setSaving(false);
      }
    },
    [tagId, apply, onMutated]
  );

  const destroy = useCallback(async () => {
    setSaving(true);
    setErrors([]);

    try {
      await deleteTag(tagId);
      onMutated();
      onClose();
    } catch (error) {
      setErrors(messagesFrom(error, t('drawer.errors.destroy')));
    } finally {
      setSaving(false);
    }
  }, [tagId, onMutated, onClose]);

  return { tag, name, setName, loading, saving, errors, rename, detach, destroy };
}
